import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/shared/api/errors';
import { notifyApiErrorIfUnhandled, suppressApiErrorToast } from '@/shared/api/httpClient';
/**
 * 补报「请求已成功、但响应校验失败」的查询与变更错误。
 *
 * 请求层只上报它自己产生的失败；Zod 校验失败是在 api 模块里抛出的，不会经过拦截器，
 * 因此这里补一次上报，保证接口相关的失败在任何页面都有可见反馈。
 * 已经是 ApiError 的失败交给请求层，避免同一次失败被上报两次。
 *
 * @param error 查询或变更抛出的错误。
 */
function reportResponseFailure(error) {
    if (error instanceof ApiError)
        return;
    notifyApiErrorIfUnhandled(error);
}
/**
 * 统一的查询与变更失败处理。
 *
 * 页面已经用错误态与重试入口承载失败的资源标记 `silentApiError`：
 * 此时只需同步标记已处理，即可挡住请求层延后投递的兜底 toast。
 *
 * @param error 查询或变更抛出的错误。
 * @param meta 该查询或变更声明的元信息。
 */
function handleQueryFailure(error, meta) {
    if (meta?.silentApiError === true) {
        suppressApiErrorToast(error);
        return;
    }
    reportResponseFailure(error);
}
/** 全局查询客户端，为交互式编辑采用保守的失败重试策略。 */
export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => {
            handleQueryFailure(error, query.meta);
        },
    }),
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
            handleQueryFailure(error, mutation.meta);
        },
    }),
    defaultOptions: {
        queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
        mutations: { retry: 0 },
    },
});
/**
 * 清空全局查询缓存，登出与会话失效时调用。
 * 不清空会让上一个账号的服务端数据残留在缓存中：会话已切换，
 * 页面仍从缓存读到旧项目数据，直到刷新重建客户端才恢复正常。
 */
export function clearAppQueryCache() {
    queryClient.clear();
}
